<template>
  <ux-molecules-search-bar :current-view="view" :hide-filters-btn="hideFiltersBtn" :location="label" />
  <template v-if="view === RestaurantsView.List">
    <section class="Layout">
      <div class="position-flex position-flex--vtop g-4 position-flex--space-between Restaurants-views__title">
        <ux-atoms-typo as="h1" color="dark" variant="expressive-heading-04">
          {{ numberOfResultsLabel }}
        </ux-atoms-typo>
        <ux-atoms-map-list-switch :current-view="view" />
      </div>
    </section>
    <ux-molecules-grid-card-restaurant :cards="cards" :columns="3" :page-name="pageName" />
  </template>
  <template v-else>
    <div class="Map position-relative">
      <ux-atoms-map-list-switch :current-view="view" />
      <ux-molecules-map-overlay-container :cards="clusters" :label="numberOfResultsLabel" />
      <ux-molecules-map
        :box="boxMapped"
        :center="mapCenter"
        :city-name="label"
        :first-restaurant-id="firstResultId"
        :markers="mapMarkersList"
        display-geo
      />
    </div>
  </template>
</template>
<script lang="ts" setup>
import type { FilterResponse, Maybe, RestaurantSearchResponse } from '~/graphql';

import { VariantType, type cardListType, cardType } from '~/components/ux/molecules/CardRestaurant/types';
import { coordType, markerType } from '~/components/ux/molecules/Map/types';
import { RestaurantsView } from '~/core';
import { gtmEvent } from '~/helpers/gtm';

export interface RestaurantsViewProps {
  isPoiPage?: boolean;
  label: string;
  pageName: null | string;
  searchRestaurantsResults: RestaurantSearchResponse;
  view: RestaurantsView;
}

const props = defineProps<RestaurantsViewProps>();

const emits = defineEmits(['filterChange']);

const { $onAction, $subscribe, getUpdatedRestaurantsResults, updateFilterData } = searchRestaurantStore();
const searchEngineStore = useSearchEngineStore();
const geoLoc = useGeolocationStore();
const { t } = useI18n();
const localePath = useLocalePath();

const filterData = ref(props.searchRestaurantsResults?.filters);
const restaurantsResults = computed({
  get: () => props.searchRestaurantsResults.results,
  set: (value) => value
});
const { searchParams } = useSearchRestaurant();

const query = {
  date: searchParams.value.date,
  guests: searchParams.value.groupSize,
  ...(props.isPoiPage ? { poi: props.label } : {})
};

const cards = computed(
  () =>
    restaurantsResults.value?.map((i) => ({
      address: `${i?.city} ${i?.zipCode}`,
      alt: t('ux.molecules.sliderCardRestaurant.altCardImage', { restaurant: i?.name }),
      availabilities: i?.availabilities,
      averageRating: i?.averageRating,
      bookingWidgetUrl: i?.bookingWidgetUrl,
      currency: i?.currency,
      enrolmentPrograms: i?.enrolmentPrograms,
      facetCodes: i?.facetCodes,
      foodType: i?.foodType,
      hotelCode: i?.hotelCode as string,
      href: localePath({ name: 'restaurant-restaurantCode', params: { restaurantCode: i?.id }, query }),
      id: i?.id,
      identifiers: i?.identifiers,
      lat: i?.lat,
      lon: i?.lon,
      medias: i?.medias,
      partnerId: i?.partnerId,
      phone: i?.phonePrefix && i.phone ? `+${i?.phonePrefix}${i?.phone}` : undefined,
      price: i?.averagePrice,
      reviewCount: i?.reviewCount,
      src: i?.imageUrl,
      stars: i?.star,
      title: i?.name,
      variant: props.view === RestaurantsView.List ? VariantType.List : VariantType.Map
    })) as unknown as cardListType
);

const numberOfResultsLabel = computed(() =>
  cards.value?.length
    ? props.label === `${geoLoc.lat},${geoLoc.lon}`
      ? t('pages.city.numberOfResultsNearMe', { n: cards.value.length })
      : t('pages.city.numberOfResults', { n: cards.value.length, place: props.label })
    : t('pages.city.noResult')
);

const center = props.searchRestaurantsResults?.center;
const box = props.searchRestaurantsResults?.box;

const boxMapped = computed(() => {
  return (
    box && {
      bottomRight: {
        lat: box?.bottomRight?.lat,
        lng: box?.bottomRight?.lon
      } as coordType,
      topLeft: {
        lat: box?.topLeft?.lat,
        lng: box?.topLeft?.lon
      } as coordType
    }
  );
});

const mapCenter = computed(() => ({
  lat: center?.lat,
  lng: center?.lon
}));

const clusters = computed(() => {
  if (!cards.value) return [];

  const clustersOrderedArray: cardType[][] = [];

  for (const element of cards.value) {
    const findIndex = clustersOrderedArray.findIndex((item) => item[0].hotelCode === element.hotelCode);

    if (findIndex !== -1) {
      clustersOrderedArray[findIndex].push(element as unknown as cardType);
    } else {
      clustersOrderedArray.push([element as unknown as cardType]);
    }
  }

  return clustersOrderedArray;
});

const firstResultId = clusters.value?.[0]?.[0].id;

const checkingCountResults = (value?: null | number) => value && value > 0;

const hideFiltersBtn = computed(() => {
  return ['FOOD_PREFERENCES', 'STYLE_OF_FOOD', 'THEMATIC'].every((filter: string) => {
    const key = filter as keyof typeof props.searchRestaurantsResults.filters;
    return !(props.searchRestaurantsResults.filters?.[key] as unknown as Maybe<Array<Maybe<FilterResponse>>>)?.some((filter) =>
      checkingCountResults(filter?.count)
    );
  });
});

const mapMarkersList = computed(() =>
  clusters.value?.map(
    (i) =>
      ({
        count: i.length,
        id: i[0].id,
        name: i.map((item) => item.title),
        position: { lat: i[0].lat, lng: i[0].lon }
      }) as markerType
  )
);

$subscribe((_mutation, state) => {
  if (state.filterInput) {
    emits('filterChange', state.filterInput);
  }
});

$onAction(({ name }) => {
  if (name === 'submitFilterInput') {
    restaurantsResults.value = getUpdatedRestaurantsResults();
  }
});

onBeforeMount(() => {
  nextTick(() => {
    if (filterData?.value) {
      updateFilterData(filterData?.value);
    }
  });
});

onMounted(() => {
  if (!restaurantsResults?.value?.length) {
    gtmEvent('bloc_interact', {
      bloc_interaction: searchEngineStore.getCurrentInput()?.toLowerCase(),
      bloc_name: 'no results',
      pagename: `restaurantsandbars::results::${props.view}`
    });
  }
});
</script>
<style scoped lang="scss">
@use 'RestaurantsView.scss';
</style>
